import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/components/ui/cn';
import type { Question } from '../types';
import { CLIMATE_IMAGES, CLIMATE_HINTS } from '../climate-images';
import { uiLang } from '@/i18n/lang';

export interface ImagePickerProps {
  question: Question;
  value: string[];
  onChange: (next: string[]) => void;
  /** Max simultaneously selected tiles. */
  max: number;
  className?: string;
}

// Fallback gradient for tiles without a photo (e.g. "any" option).
const FALLBACK_GRADIENTS: Record<string, string> = {
  any: 'bg-gradient-to-br from-paper-2 to-paper',
};

/**
 * Multi-select image tile grid for the climate question (Q2).
 *
 * Quiet Atlas treatment: paper card body, ink-100 default border, terra-600
 * border + terra-100 ring on selection. Background uses real photography
 * via Unsplash so users without meteorological background can pick by feel.
 */
export function ImagePicker({ question, value, onChange, max, className }: ImagePickerProps) {
  const { t, i18n } = useTranslation();
  const options = question.options ?? [];
  const limitReached = value.length >= max;
  const lang = uiLang(i18n.language);
  const [failedImages, setFailedImages] = useState(new Set<string>());
  const handleImgError = (optId: string) =>
    setFailedImages((prev) => new Set([...prev, optId]));

  const toggle = (optionId: string) => {
    if (value.includes(optionId)) {
      onChange(value.filter((id) => id !== optionId));
      return;
    }
    if (limitReached) return;
    onChange([...value, optionId]);
  };

  return (
    <div
      role="group"
      aria-label={t(question.label_key)}
      className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3', className)}
    >
      {options.map((opt) => {
        const selected = value.includes(opt.id);
        const disabled = !selected && limitReached;
        const photoUrl = CLIMATE_IMAGES[opt.id];
        const hint = CLIMATE_HINTS[opt.id]?.[lang];
        const fallbackGradient =
          FALLBACK_GRADIENTS[opt.id] ?? 'bg-gradient-to-br from-paper-2 to-paper';
        const imgFailed = failedImages.has(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            role="checkbox"
            aria-checked={selected}
            aria-disabled={disabled || undefined}
            disabled={disabled}
            onClick={() => toggle(opt.id)}
            className={cn(
              'group relative flex min-h-[140px] flex-col overflow-hidden rounded-lg border text-left',
              'transition-all focus:outline-none focus:ring-2 focus:ring-terra-600/30 focus:ring-offset-2 focus:ring-offset-paper',
              selected
                ? 'border-terra-600 ring-2 ring-terra-100'
                : 'border-ink-100 hover:border-ink-300',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {/* Fixed-aspect image box: every tile's photo is the same height
                regardless of how many lines the hint below runs to. */}
            <span className="block aspect-[4/3] w-full overflow-hidden">
              {photoUrl && !imgFailed ? (
                <img
                  src={photoUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="eager"
                  decoding="async"
                  onError={() => handleImgError(opt.id)}
                />
              ) : (
                <span className={cn('block h-full w-full', fallbackGradient)} aria-hidden="true" />
              )}
            </span>
            <span className="flex-1 border-t border-ink-100 bg-paper px-3 py-2.5">
              <span className="block font-sans text-sm font-semibold text-ink-900">
                {t(opt.label_key)}
              </span>
              {hint && (
                <span className="mt-0.5 block text-[11px] leading-snug text-ink-500">
                  {hint}
                </span>
              )}
            </span>
            {selected && (
              <span
                aria-hidden="true"
                className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-terra-600 text-paper shadow"
                data-testid={`imagepicker-check-${opt.id}`}
              >
                <Check className="h-4 w-4" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
